"use client";

import { ActionIcon, useMantineColorScheme } from "@mantine/core";
import { type ActionIconProps } from "@mantine/core";
import * as React from "react";
import { LuMoon, LuSun } from "react-icons/lu";
import { Skeleton } from "@mantine/core";
import { useState } from "react";
import { useEffect } from "react";

export type ColorMode = "light" | "dark";

export interface UseColorModeReturn {
  colorMode: ColorMode;
  setColorMode: (colorMode: ColorMode) => void;
  toggleColorMode: () => void;
}

export function useColorMode(): UseColorModeReturn {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const toggleColorMode = () => {
    setColorScheme(colorScheme === "dark" ? "light" : "dark");
  };
  return {
    colorMode: colorScheme as ColorMode,
    setColorMode: setColorScheme as (colorMode: ColorMode) => void,
    toggleColorMode,
  };
}

export function useColorModeValue<T>(light: T, dark: T) {
  const { colorMode } = useColorMode();
  return colorMode === "dark" ? dark : light;
}

export function ColorModeIcon() {
  const { colorMode } = useColorMode();
  return colorMode === "dark" ? <LuMoon /> : <LuSun />;
}

export const ColorModeButton = React.forwardRef<HTMLButtonElement, ActionIconProps>(
  function ColorModeButton(props, ref) {
    const { toggleColorMode } = useColorMode();
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
      setMounted(true);
    }, []);

    if (!mounted) return <Skeleton height={32} width={32} />;

    return (
      <ActionIcon
        onClick={toggleColorMode}
        variant="subtle"
        aria-label="Toggle color mode"
        size="sm"
        ref={ref}
        {...props}
      >
        <ColorModeIcon />
      </ActionIcon>
    );
  },
);
